import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { ApolloClient, InMemoryCache } from '@apollo/client';
import { Trans } from '@lingui/react';
import { t } from '@lingui/macro';
import { addMinutes } from 'date-fns/addMinutes';
import RegisterFormTemplate from '@ecosystems/registerForm.pageTemplate';
import { Box, Flex } from '@components/layout/Grid';
import MetaTags from '@components/MetaTags';
import {
  LiveEventDocument,
  LiveEventQuery,
  useLiveEventQuery,
  LiveEvent,
  LiveEventPaymentKind,
  LiveEventKind,
} from '@gql/generated';
import Spinner from '@components/Spinner';
import redirect from '@lib/redirect';
import CookieManager from '@lib/CookieManager';
import { EVENT_VISIT_DATE_COOKIE } from '@lib/constants';
import { ReserveCountdown } from '@ecosystems/events/reserve/ReserveCountdown';
import { PaymentForm } from '@ecosystems/events/reserve/PaymentForm';
import { BuyGiftForm } from '@ecosystems/events/reserve/BuyGiftForm';
import tabs from '@organisms/Tabs';
import Link from 'next/link';
import SvgArrowRightIcon from '@assets/ArrowRightIcon';
import {
  CurrencyButton,
  LanguageDropdown,
} from '@organisms/modals/CurrencyLanguageModal';

const Wrapper = styled(RegisterFormTemplate.Wrapper)`
  /* max-height: 100vh;
  overflow-y: scroll; */
`;

const RightContainer = styled(RegisterFormTemplate.RightContainer)`
  background-color: ${({ theme }) => theme.colors.neutrals.white};
  // overflow-x: hidden;
`;

const RightBox = styled.div`
  flex: 1;
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  justify-content: start;
  padding: 30px 20px;

  @media (min-width: ${({ theme }) => theme.viewports.laptop}px) {
    padding: 40px 30px 20px;
  }
`;

const LeftContainer = styled(RegisterFormTemplate.LeftContainer)`
  width: 100%;
  max-width: 600px;
  padding: 10vh 20px 0;
  margin: 0 auto;
  background-color: ${({ theme }) => theme.colors.neutrals.white};
  /* must be kept for the line in subscription form */
  z-index: 1;

  @media (min-width: ${({ theme }) => theme.viewports.laptop}px) {
    position: sticky;
    top: 0;
    min-height: 100vh;
    max-width: 100vh;
    background-image: url('/static/photo-1562088287-bde35a1ea917-2.jpeg');
    background-size: cover;
    background-repeat: no-repeat;
    background-color: transparent;
  }
`;

const LeftBox = styled.div`
  max-width: 600px;
  width: 100%;
  padding-top: 30px; // for countdown banner
  box-sizing: content-box;

  @media (min-width: ${({ theme }) => theme.viewports.laptop}px) {
    text-align: center;
    justify-self: unset;
    margin-right: 10%;
  }
`;

const TabsContainer = styled(Flex)`
  border-bottom: 2px solid lightgray;
`;

const Tabs = styled(tabs)`
  padding: 10px 0;
  margin-bottom: -1px;
`;

const GoBack = styled(Link)`
  display: flex;
  color: ${({ theme }) => theme.colors.neutrals.black};
  font-weight: 500;

  svg {
    width: 16px;
    margin-right: 5px;
    transform: rotateY(180deg);
  }
`;

const LiveEventReserve = ({ pageProps }) => {
  const router = useRouter();
  const pageId = router.query.pageId as string;
  const slug = router.query.slug as string;
  const lang = router.query.lang as string;

  const [showCountDown, setShowCountDown] = useState(false);
  const { data, loading, error } = useLiveEventQuery({
    variables: { slug },
  });

  useEffect(() => {
    setShowCountDown(true);
    CookieManager.set(
      {
        key: EVENT_VISIT_DATE_COOKIE,
        value: pageProps?.visitDate,
        path: router.asPath,
      },
      addMinutes(new Date(pageProps?.visitDate), 600)
    );

    return () => {
      CookieManager.remove({ key: EVENT_VISIT_DATE_COOKIE });
    };
  }, []);

  if (error) {
    return 'something unexpected happened!!';
  } else if (!['buy-gift', 'reserve'].includes(pageId)) {
    if (typeof window !== 'undefined') {
      router.push(`/${lang}/live/${slug}`);
    }
    return null;
  }

  const event = (data?.liveEvent || pageProps?.ssrEvent) as LiveEvent;

  if (loading && !event) {
    return (
      <Flex justifyContent="center" alignItems="center" my={50}>
        <Spinner size={28} spinnerColor="#242424" />
      </Flex>
    );
  } else if (!event) {
    return null;
  }

  const tabs = [
    {
      name: <Trans id="live.payment_page.tabs.reserve" />,
      value: 'reserve',
    },
  ];

  if (
    ![
      LiveEventPaymentKind['FreeForAll'],
      LiveEventPaymentKind['CampaignLive'],
      LiveEventPaymentKind['PremiumLive'],
    ].includes(event?.paymentKind) &&
    data?.liveEvent?.kind !== LiveEventKind['Premium']
  ) {
    tabs.push({
      name: <Trans id="live.payment_page.tabs.buy_gift" />,
      value: 'buy-gift',
    });
  }

  return (
    <>
      <MetaTags
        title="Yoga och träning online för hållbar hälsa"
        description={t({
          id: 'live.live_page.metadata.desc',
          values: {
            instructor: event.primaryInstructor?.name,
            eventName: event.title,
          },
        })}
        thumbnailUrl="/static/live-meta-image.jpg"
      />
      <Wrapper>
        <LeftContainer>
          <LeftBox>
            <RegisterFormTemplate.Logo whiteOnDesktop />
          </LeftBox>
        </LeftContainer>
        <RightContainer>
          {showCountDown ? (
            <ReserveCountdown visitDate={pageProps?.visitDate} />
          ) : null}
          <RightBox>
            <Box mb={20}>
              <GoBack href={`/${lang}/live/${slug}`}>
                <SvgArrowRightIcon />
                <Trans id="metadata.go_back_simple" />
              </GoBack>
            </Box>
            <TabsContainer justifyContent="flex-end" mb={30}>
              <Tabs
                tabs={tabs}
                onTabSelect={(value) => {
                  //
                  const url = { pathname: `/${lang}/live/${slug}/${value}` };
                  router.replace(url, url, { shallow: true });
                }}
                selectedTab={pageId}
              />
            </TabsContainer>
            {pageId === 'reserve' ? <PaymentForm event={event} /> : null}
            {pageId === 'buy-gift' ? <BuyGiftForm event={event} /> : null}
          </RightBox>
          <div className="w-full max-w-[600px] px-6 py-6 flex gap-3 md:gap-6">
            <LanguageDropdown />
            <CurrencyButton />
          </div>
        </RightContainer>
      </Wrapper>
    </>
  );
};

export const getServerSideProps = async (ctx) => {
  const { lang, slug, pageId } = ctx.params;
  const apolloClient = ctx.req.apolloClient as ApolloClient<InMemoryCache>;

  if (!['reserve', 'buy-gift'].includes(pageId)) {
    return {
      props: {},
      redirect: {
        destination: `/${lang}/live/${slug}`,
        permanent: true,
      },
    };
  }

  const visitDate = CookieManager.get({
    key: EVENT_VISIT_DATE_COOKIE,
    req: ctx.req,
  });

  if (!apolloClient?.query) {
    return {
      props: {
        visitDate: visitDate
          ? new Date(visitDate).toISOString()
          : new Date().toISOString(),
      },
    };
  }

  const { data: liveData } = await apolloClient.query<LiveEventQuery>({
    query: LiveEventDocument,
    variables: { slug },
  });

  if (!liveData.liveEvent) {
    redirect(ctx, `/${lang}/live`);
  }

  return {
    props: {
      ssrEvent: liveData.liveEvent,
      visitDate: visitDate
        ? new Date(visitDate).toISOString()
        : new Date().toISOString(),
    },
  };
};

LiveEventReserve.getPageConfig = () => {
  return {
    layout: 'none',
  };
};

export default LiveEventReserve;
